import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { EntityIssuesType } from 'hooks/useEntityIssues';
import type { ITestSession } from 'api/concierge';

export interface IConciergeIssuesTarget {
    type: EntityIssuesType;
    id: number;
    // Set by the report viewer when the report has AI Insights on: the panel then shows the report's
    // AI Insight; insightId is a burst/email deep link's ?insight=.
    viewer?: { insightId: number | null };
}

// The semantic model whose editor page is open: its published report rides every ordinary ask as
// element_id (after an explicit launch element) so the ask answers on that model first and falls
// back to the general path, the way it does from the report viewer (MI-30532). A model with no
// published report keeps its name for the header and sends no element.
export interface IConciergeEditorContext {
    elementId: number | null;
    modelName: string;
}

export interface ITool {
    name: string;
    icon: string | {};
    title: string;
}

export interface ICustomService {
    name: string;
    domain_id?: string;
    authInfo?: {
        authUrl: string;
        remainDays: number;
        providerName: string;
        tokenFieldName: string;
    };
}

/** Built-in panel sources: Tiles search and the documentation-style sources. Everything else is a Domain. */
export const BUILT_IN_SOURCE_NAMES = ['search', 'glossary', 'faq', 'docs'];
/** The "Report Data" entry of the source selector (Concierge_Panel.md §6.4): the knowledge-graph models and datasets the user can access. */
export const REPORT_DATA_SOURCE_NAME = 'report_data';
/** Where the panel is open: off a report, over a report Concierge can read, over one it cannot (warning). */
export type ReportDataState = 'no_report' | 'enabled' | 'no_access';

export interface IDomain {
    concierge_domain_id: string;
    name: string;
    icon_ind: string;
    icon_name: string;
    icon_data: string;
    title: string;
    description?: string;
    suggestedPrompts?: string[];
    customServices?: ICustomService[];
    reportDataState?: ReportDataState;
}

export interface MappedDomains {
    [key: string]: string[];
}

export interface IConciergeCustomEvents {
    owner_click?: boolean;
    certified_username_click?: boolean;
}

export interface conciergeSliceState {
    visible: boolean;
    closeChat: boolean;
    onlySearch: boolean;
    isAppPanel: boolean;
    isMobile: boolean;
    fromSearch: boolean;
    width: number;
    // Horizontal room (px from the right edge) the clarification toast asks
    // other bottom-right widgets to leave; 0 while no toast is mounted.
    inputToastRightClearance: number;
    newChat: string | null;
    availableDomains: IDomain[];
    availableTools: ITool[];
    selectedTools: string[];
    selectedDomains: string[];
    defaultSelectedDomains: string[] | null | undefined;
    focusInput: boolean;
    customStyles: any;
    customEvents: IConciergeCustomEvents;
    messageDomains: { [messageUid: string]: string };
    authorizedTools: string[];
    newRequestMessage: string | null;
    prependContext: { text: string; showInChat: boolean } | null;
    issuesTarget: IConciergeIssuesTarget | null;
    launchElementId: number | null;
    editorContext: IConciergeEditorContext | null;
    targetQuestionId: string | null;
    elementConciergeVisibility: 'visible' | 'hidden' | null;
    testSession: ITestSession | null;
}

export const conciergeSlice = createSlice({
    name: 'concierge',
    initialState: {
        visible: false,
        closeChat: false,
        width: 420,
        inputToastRightClearance: 0,
        newChat: null,
        onlySearch: false,
        fromSearch: false,
        availableTools: [],
        availableDomains: [],
        selectedTools: [],
        selectedDomains: [],
        defaultSelectedDomains: null,
        focusInput: false,
        customStyles: {},
        customEvents: {},
        isAppPanel: false,
        isMobile: false,
        messageDomains: {},
        authorizedTools: [],
        newRequestMessage: null,
        prependContext: null,
        issuesTarget: null,
        launchElementId: null,
        editorContext: null,
        targetQuestionId: null,
        elementConciergeVisibility: null,
        testSession: null,
    } as conciergeSliceState,
    reducers: {
        show: (state) => {
            state.visible = true;
        },
        hide: (state) => {
            state.visible = false;
            state.launchElementId = null;
        },
        toggleVisibility: (state) => {
            state.visible = !state.visible;
            if (!state.visible) {
                state.launchElementId = null;
            }
        },
        setConciergeWidgetWidth: (state, action) => {
            state.width = action.payload;
        },
        startNewChat: (state, action) => {
            state.newChat = action.payload;
        },
        clearNewChat: (state) => {
            state.newChat = null;
        },
        sendNewRequestMessage: (state, action) => {
            state.newRequestMessage = action.payload;
        },
        clearNewRequestMessage: (state) => {
            state.newRequestMessage = null;
        },
        closeChat: (state, action) => {
            state.closeChat = action.payload;
        },
        setOnlySearch: (state, action) => {
            state.onlySearch = action.payload;
        },
        setAvailableDomains: (state, action) => {
            state.availableDomains = action.payload;
        },
        setAvailableTools: (state, action) => {
            state.availableTools = action.payload;
        },
        setSelectedDomains: (state, action) => {
            state.selectedDomains = action.payload;
            state.focusInput = !state.focusInput;
        },
        setFromSearch: (state, action) => {
            state.fromSearch = action.payload;
        },
        setDefaultSelectedDomains: (state, action) => {
            state.defaultSelectedDomains = action.payload;
        },
        setInputToastRightClearance: (state, action: PayloadAction<number>) => {
            state.inputToastRightClearance = action.payload;
        },
        setCustomStyles: (state, action) => {
            state.customStyles = action.payload;
        },
        setIsAppPanel: (state, action) => {
            state.isAppPanel = action.payload;
        },
        setConciergeCustomEvents: (state, action: PayloadAction<IConciergeCustomEvents>) => {
            state.customEvents = action.payload;
        },
        setIsMobile: (state, action) => {
            state.isMobile = action.payload;
        },
        setMessageDomain: (state, action) => {
            const { messageUid, domainName } = action.payload;
            state.messageDomains[messageUid] = domainName;
        },
        addAuthorizedTool: (state, action) => {
            const toolName = action.payload;
            if (!state.authorizedTools.includes(toolName)) {
                state.authorizedTools.push(toolName);
            }
        },
        setPrependContext: (state, action) => {
            state.prependContext = action.payload;
        },
        clearPrependContext: (state) => {
            state.prependContext = null;
        },
        setIssuesTarget: (state, action: PayloadAction<IConciergeIssuesTarget | null>) => {
            state.issuesTarget = action.payload;
        },
        // The catalog entry (or report) the panel was opened from; rides every ask as element_id
        // until the panel closes.
        setLaunchElement: (state, action: PayloadAction<number | null>) => {
            state.launchElementId = action.payload && action.payload > 0 ? action.payload : null;
        },
        // Owned by the editor page: set while it is mounted, cleared when it unmounts — never on
        // hide, so a re-open through the floating button still answers on the model.
        setEditorContext: (state, action: PayloadAction<IConciergeEditorContext | null>) => {
            state.editorContext = action.payload;
        },
        setElementConciergeVisibility: (state, action: PayloadAction<'visible' | 'hidden' | null>) => {
            state.elementConciergeVisibility = action.payload;
        },
        setTargetQuestionId: (state, action: PayloadAction<string | null>) => {
            state.targetQuestionId = action.payload;
        },
        clearTargetQuestionId: (state) => {
            state.targetQuestionId = null;
        },
        setTestSession: (state, action: PayloadAction<ITestSession>) => {
            state.testSession = action.payload;
        },
        clearTestSession: (state) => {
            state.testSession = null;
        },
    },
});

export const {
    show,
    hide,
    toggleVisibility,
    setConciergeWidgetWidth,
    startNewChat,
    clearNewChat,
    closeChat,
    setOnlySearch,
    setAvailableDomains,
    setSelectedDomains,
    setAvailableTools,
    setFromSearch,
    setDefaultSelectedDomains,
    setInputToastRightClearance,
    setCustomStyles,
    setIsAppPanel,
    setIsMobile,
    setMessageDomain,
    addAuthorizedTool,
    sendNewRequestMessage,
    clearNewRequestMessage,
    setPrependContext,
    clearPrependContext,
    setConciergeCustomEvents,
    setIssuesTarget,
    setLaunchElement,
    setEditorContext,
    setElementConciergeVisibility,
    setTargetQuestionId,
    clearTargetQuestionId,
    setTestSession,
    clearTestSession,
} = conciergeSlice.actions;

export default conciergeSlice.reducer;
